import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { captureError } from '@/lib/sentry';

/**
 * Per-recipient failure log for ONE flagged campaign, for the enterprise
 * monitoring detail Sheet. Wraps the SECURITY-DEFINER read RPC
 * `partner_campaign_failures`, which derives the owning partner from the
 * campaign's workspace and re-authorizes server-side on `email_monitor` — so
 * this hook is a thin data layer, not a security boundary.
 *
 * Paginated: one RPC call per page (25 rows), accumulated across "Load more"
 * via useInfiniteQuery. `enabled` is driven by the caller (Sheet open AND
 * emails_failed > 0) so no query fires for healthy campaigns.
 */

/** One failed recipient — columns match partner_campaign_failures RETURNS TABLE. */
export interface FailureRow {
  recipient_email: string;
  reason: string | null;
  response_code: number | null;
  job_status: string;
  attempts: number;
  last_attempt_at: string;
}

export const FAILURES_PAGE_SIZE = 25;

export function useEnterpriseCampaignFailures(
  campaignId: string,
  { enabled }: { enabled: boolean },
) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.enterprisePartners.campaignFailures(campaignId),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await supabase.rpc('partner_campaign_failures', {
        p_campaign_id: campaignId,
        p_limit: FAILURES_PAGE_SIZE,
        p_offset: pageParam,
      });
      if (error) {
        captureError(error as unknown as Error, {
          feature: 'enterprise-campaign-failures',
        });
        throw error;
      }
      return (data ?? []) as FailureRow[];
    },
    getNextPageParam: (lastPage, allPages) =>
      // Stop when the last page came back short — no more rows to fetch.
      lastPage.length < FAILURES_PAGE_SIZE
        ? undefined
        : allPages.length * FAILURES_PAGE_SIZE,
    enabled: enabled && !!campaignId,
  });

  // Flatten accumulated pages into a single newest-first list for the UI.
  const rows: FailureRow[] = query.data?.pages.flat() ?? [];

  return {
    rows,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}
